import { HTTP_INTERCEPTORS, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { EnvironmentProviders, Injectable, Provider } from '@angular/core';
import { LoginService } from '@microfrontend-toolkit/login-service';
import { LOGIN_SERVICE_KEY } from '@microfrontend-toolkit/login-service/constants';
import { ServiceProvider, ServiceProviderFactory } from '@microfrontend-toolkit/service-provider';
import { Observable, switchMap } from 'rxjs';
import { fromPromise } from 'rxjs/internal/observable/innerFrom';
import { apimApiVersion, authInterceptorIgnoredUrls } from 'src/environments/environment';

export const InterceptorSkipHeader = 'X-Skip-Interceptor';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private authToken: string | undefined;
  private serviceProvider: ServiceProvider | undefined;
  private loginService: LoginService | undefined;

  constructor() {
    this.initLogin();
  }

  public intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (req.headers.has(InterceptorSkipHeader)) {
      const headers = req.headers.delete(InterceptorSkipHeader);
      return next.handle(req.clone({ headers }));
    }

    if (authInterceptorIgnoredUrls.some(url => req.url.includes(url))) {
      return next.handle(req);
    }

    // Local config
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get("jwt")) {
      this.authToken = searchParams.get("jwt") as string | undefined;

      return this.prepareInterception(req, next);
    }

    if (!this.loginService) {
      // Handle the case when loginService is not defined
      return next.handle(req);
    }

    const jwtToken$ = fromPromise(this.loginService.getLoginToken());

    return jwtToken$.pipe(
      switchMap(jwt => {
        this.authToken = jwt;

        return this.prepareInterception(req, next);
      })
    );
  }

  private initLogin(): void {
    const serviceProviderHost: HTMLElement = document.querySelector('#root') as HTMLElement;
    this.serviceProvider = ServiceProviderFactory.getServiceProvider({
      mountOptions: { element: serviceProviderHost },
    });


    this.loginService = this.serviceProvider?.getService<LoginService>(LOGIN_SERVICE_KEY);
  }

  private prepareInterception(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Check if req already contains a Authorization header
    if (req.headers.has('Authorization')) {
      return next.handle(req);
    }

    if (this.authToken) {
      // Clone the request and replace the original headers with
      // cloned headers, updated with the authorization
      const authReq = req.clone({
        headers: req.headers.set(
          'Authorization',
          this.authToken.startsWith('Bearer ') ? this.authToken : `Bearer ${this.authToken!}`
        ),
        params: apimApiVersion ? req.params.append(
          'api-version', apimApiVersion
        ) : req.params,
      });
      // send cloned request with header to the next handler
      return next.handle(authReq);
    } else {
      return next.handle(req);
    }
  }
}

export const AuthInterceptorProvider: Provider | EnvironmentProviders = {
  provide: HTTP_INTERCEPTORS,
  useClass: AuthInterceptor,
  multi: true,
};
